// ----------------------------------------------------------------------
// Environment Configuration
// All import.meta.env access is centralized here
// ----------------------------------------------------------------------

// App environment helpers (getAppEnv/isDev/isStaging/isProduction) are shared
// across apps and live in @nexus/ari-lib. Re-exported below so existing
// `import { ... } from './config/global'` call sites keep working. Imported
// from the dedicated module path (not the package barrel) so config/global
// doesn't pull the entire ari-lib graph into every consumer.
export { type AppEnvironment, getAppEnv, isDev, isStaging, isProduction } from '@nexus/ari-lib/app-env';

const env = {
  // Feature flags. Each is on only where its .env file sets it to "true".
  FEATURE_SKILL_WORKFLOWS: import.meta.env.VITE_FEATURE_SKILL_WORKFLOWS === 'true',

  // Supabase
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL || '',
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
  // The Supabase project's region. Documents calls run there. CLOUD-958.
  SUPABASE_FUNCTION_REGION: (import.meta.env.VITE_SUPABASE_FUNCTION_REGION || '').trim(),

  // App URLs
  ARI_URL: import.meta.env.VITE_ARI_URL || 'http://localhost:4200',
  ARI_CLOUD_URL: import.meta.env.VITE_ARI_CLOUD_URL || 'http://localhost:4201',
  ARI_BUSINESS_URL: import.meta.env.VITE_ARI_BUSINESS_URL || 'https://business.ari-os.com',

  // Ari Business self-service registration wizard — where a signed-in user with
  // no workspace is sent. The BC IDP's own sso-error page still links the
  // realwear.com host; reconciling that is CLOUD-688.
  GET_STARTED_URL: import.meta.env.VITE_GET_STARTED_URL || 'https://get-started.ari-os.com',

  // ARI python backend (skill creator). Dev-only override; staging/prod
  // resolve the geo-routed server from the /ari/url edge function instead.
  ARI_SERVER_URL: import.meta.env.VITE_ARI_SERVER_URL || 'http://localhost:8787',

  // Subscription-manager checkout API. Defaults to staging
  // but the URL should be set in Github Secrets for all envs
  SUBSCRIPTION_MANAGER_URL: import.meta.env.VITE_SUBSCRIPTION_MANAGER_URL || 'https://api.account.ari-os.review',

  // Auth0 tenant that owns the device code flow.
  // These vars are used to build the activate link (including a pre-logout that clears the cookies)
  AUTH0_DOMAIN: import.meta.env.VITE_AUTH0_DOMAIN || '',
  AUTH0_DEVICE_CLIENT_ID: import.meta.env.VITE_AUTH0_DEVICE_CLIENT_ID || '',
} as const;

// ----------------------------------------------------------------------
// Exported Configuration
// ----------------------------------------------------------------------

export const AppVersion = '1.0.0';

export const getSupabaseConfig = () => ({
  url: env.SUPABASE_URL,
  anonKey: env.SUPABASE_ANON_KEY,
});

export const getSupabaseUrl = () => env.SUPABASE_URL;

export const getSupabaseFunctionRegion = () => env.SUPABASE_FUNCTION_REGION;

const isLocalSupabase = () => {
  try {
    const host = new URL(env.SUPABASE_URL).hostname;
    return host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
  } catch {
    return false;
  }
};

/**
 * Get the auth backend URL for Auth0 login/connected accounts.
 * Uses Vite proxy only when connecting to local Supabase (bypasses Kong's default CORS).
 */
export const getAuthBackendUrl = () => {
  if (isLocalSupabase()) {
    return '/supabase-functions/root';
  }
  return `${env.SUPABASE_URL}/functions/v1/root`;
};

export const getAriUrl = () => env.ARI_URL; // ARI app URL

export const getAriCloudUrl = () => env.ARI_CLOUD_URL; // ARI Cloud app URL

export const getAriBusinessUrl = () => env.ARI_BUSINESS_URL; // Ari Business portal

export const getGetStartedUrl = () => env.GET_STARTED_URL; // Ari Business self-service signup

export const getAriServerUrl = () => env.ARI_SERVER_URL; // ARI python backend (dev override)

export const getSubscriptionManagerUrl = () => env.SUBSCRIPTION_MANAGER_URL; // subscription-manager checkout API

/**
 * Outbound target of our /activate entry point. It hits Auth0 logout first, or
 * a live session approves the pairing code with no sign-in, and `returnTo`
 * then lands on Auth0's own device page. Null when the tenant is unset.
 */
export const getDeviceActivateUrl = (): string | null => {
  if (!env.AUTH0_DOMAIN || !env.AUTH0_DEVICE_CLIENT_ID) return null;
  const params = new URLSearchParams({
    client_id: env.AUTH0_DEVICE_CLIENT_ID,
    returnTo: `https://${env.AUTH0_DOMAIN}/activate`,
  });
  return `https://${env.AUTH0_DOMAIN}/v2/logout?${params.toString()}`;
};

/** /ari/url edge endpoint — resolves the geo-routed ARI backend on staging/prod. */
export const getAriUrlEndpoint = () => {
  if (isLocalSupabase()) {
    return '/supabase-functions/root/ari/url';
  }
  return `${env.SUPABASE_URL}/functions/v1/root/ari/url`;
};

/** The skill workflows editor and gallery (CLOUD-933). Off means the old Skills page. */
export const isSkillWorkflowsEnabled = () => env.FEATURE_SKILL_WORKFLOWS;
